import { sageBaseCameraApi as api } from './sageBaseCameraApi';

export const addTagTypes = ['cameras', 'camera', 'auth'] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getCameras: build.query<GetCamerasApiResponse, GetCamerasApiArg>({
                query: (queryArg) => ({
                    url: `/cameras`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        enterpriseRid: queryArg.enterpriseRid,
                        unitRid: queryArg.unitRid,
                        macAddress: queryArg.macAddress,
                        ipAddress: queryArg.ipAddress,
                        mode: queryArg.mode,
                        fallAlertSensitivity: queryArg.fallAlertSensitivity,
                    },
                }),
                providesTags: ['cameras'],
            }),
            createOrUpdateCamera: build.mutation<
                CreateOrUpdateCameraApiResponse,
                CreateOrUpdateCameraApiArg
            >({
                query: (queryArg) => ({
                    url: `/cameras`,
                    method: 'POST',
                    body: queryArg.items,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            createOrUpdateCamerasBulk: build.mutation<
                CreateOrUpdateCamerasBulkApiResponse,
                CreateOrUpdateCamerasBulkApiArg
            >({
                query: (queryArg) => ({
                    url: `/cameras/bulk`,
                    method: 'POST',
                    body: queryArg.bulkCameraRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            updateCamerasBulk: build.mutation<
                UpdateCamerasBulkApiResponse,
                UpdateCamerasBulkApiArg
            >({
                query: (queryArg) => ({
                    url: `/cameras/bulk`,
                    method: 'PATCH',
                    body: queryArg.bulkUpdateCameraRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            updateCamera: build.mutation<UpdateCameraApiResponse, UpdateCameraApiArg>({
                query: (queryArg) => ({
                    url: `/cameras/${queryArg.uid}`,
                    method: 'PATCH',
                    body: queryArg.updateCameraRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            deleteCamera: build.mutation<DeleteCameraApiResponse, DeleteCameraApiArg>({
                query: (queryArg) => ({
                    url: `/cameras/${queryArg.uid}`,
                    method: 'DELETE',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            getCamera: build.query<GetCameraApiResponse, GetCameraApiArg>({
                query: (queryArg) => ({
                    url: `/cameras/${queryArg.uid}`,
                    headers: { Authorization: queryArg?.authorization },
                }),
                providesTags: ['camera'],
            }),
            startCameraStream: build.mutation<
                StartCameraStreamApiResponse,
                StartCameraStreamApiArg
            >({
                query: (queryArg) => ({
                    url: `/cameras/${queryArg.uid}/stream/start`,
                    method: 'POST',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            stopCameraStream: build.mutation<StopCameraStreamApiResponse, StopCameraStreamApiArg>({
                query: (queryArg) => ({
                    url: `/cameras/${queryArg.uid}/stream/stop`,
                    method: 'POST',
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['cameras'],
            }),
            validateAdminToken: build.mutation<
                ValidateAdminTokenApiResponse,
                ValidateAdminTokenApiArg
            >({
                query: (queryArg) => ({
                    url: `/auth/validate-admin`,
                    method: 'POST',
                    body: queryArg.adminTokenValidationRequest,
                    headers: { Authorization: queryArg?.authorization },
                }),
                invalidatesTags: ['auth'],
            }),
            getCameraHealth: build.query<GetCameraHealthApiResponse, GetCameraHealthApiArg>({
                query: (queryArg) => ({
                    url: `/cameras/healthcheck`,
                    headers: { Authorization: queryArg?.authorization },
                    params: {
                        facilityRid: queryArg.facilityRid,
                        enterpriseRid: queryArg.enterpriseRid,
                        unitRid: queryArg.unitRid,
                        mode: queryArg.mode,
                        fallAlertSensitivity: queryArg.fallAlertSensitivity,
                    },
                }),
                providesTags: ['cameras'],
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageCameraApi };
export type GetCamerasApiResponse = /** status 200 OK */ GetCamerasResponse;
export type GetCamerasApiArg = {
    authorization?: string;
    facilityRid?: string;
    enterpriseRid?: string;
    unitRid?: string;
    macAddress?: string;
    ipAddress?: string;
    mode?: 'NO_ALERTS' | 'NO_ALERTS_OR_EVENTS' | 'DEFAULT' | 'DISABLED';
    fallAlertSensitivity?: number | null;
};
export type CreateOrUpdateCameraApiResponse = /** status 200 OK */ CameraDefinition;
export type CreateOrUpdateCameraApiArg = {
    authorization?: string;
    items: CameraDefinition;
};
export type CreateOrUpdateCamerasBulkApiResponse = /** status 200 OK */ BulkCameraOperationResponse;
export type CreateOrUpdateCamerasBulkApiArg = {
    authorization?: string;
    bulkCameraRequest: BulkCameraRequest;
};
export type UpdateCamerasBulkApiResponse = /** status 200 OK */ BulkCameraOperationResponse;
export type UpdateCamerasBulkApiArg = {
    authorization?: string;
    bulkUpdateCameraRequest: BulkUpdateCameraRequest;
};
export type UpdateCameraApiResponse = /** status 200 OK */ CameraDefinition;
export type UpdateCameraApiArg = {
    /** The unique identifier of the camera */
    uid: string;
    authorization?: string;
    updateCameraRequest: UpdateCameraRequest;
};
export type DeleteCameraApiResponse = unknown;
export type DeleteCameraApiArg = {
    /** The unique identifier of the camera */
    uid: string;
    authorization?: string;
};
export type GetCameraApiResponse = /** status 200 OK */ CameraDefinition;
export type GetCameraApiArg = {
    /** The unique identifier of the camera */
    uid: string;
    authorization?: string;
};
export type StartCameraStreamApiResponse = /** status 200 OK */ StreamOperationResponse;
export type StartCameraStreamApiArg = {
    /** The unique identifier of the camera */
    uid: string;
    authorization?: string;
};
export type StopCameraStreamApiResponse = /** status 200 OK */ StreamOperationResponse;
export type StopCameraStreamApiArg = {
    /** The unique identifier of the camera */
    uid: string;
    authorization?: string;
};
export type ValidateAdminTokenApiResponse =
    /** status 200 Response indicating whether the token is valid */ AdminValidationResponse;
export type ValidateAdminTokenApiArg = {
    authorization?: string;
    adminTokenValidationRequest: AdminTokenValidationRequest;
};
export type GetCameraHealthApiResponse = /** status 200 OK */ GetCameraHealthResponse;
export type GetCameraHealthApiArg = {
    authorization?: string;
    facilityRid?: string;
    enterpriseRid?: string;
    unitRid?: string;
    mode?: 'NO_ALERTS' | 'NO_ALERTS_OR_EVENTS' | 'DEFAULT' | 'DISABLED';
    fallAlertSensitivity?: number | null;
};
export type Schema = 'NO_ALERTS' | 'NO_ALERTS_OR_EVENTS' | 'DEFAULT' | 'DISABLED';
export type ConnectionType = 'POE' | 'WIFI';
export type CameraDefinition = {
    /** The unique Reolink identifier for the camera */
    uid: string;
    /** This is the human-readable name given to the camera during the unit allocation process. It may be null if
    the camera was not allocated to a unit, or the camera was allocated before we started using this field.
     */
    name?: string;
    /** The IP address of the camera. Absent for WiFi cameras that have been provisioned but not
    yet activated - their static IP is assigned at installation-time activation.
     */
    ipAddress?: string | null;
    /** The MAC address of the camera's ethernet (PoE port) interface. WiFi cameras have a separate
    MAC for the WiFi radio, which is not stored in this field.
     */
    macAddress: string;
    /** The MAC address of the camera's WiFi radio. Differs from the ethernet macAddress and is only
    discoverable once the camera has joined a WiFi network, so it is recorded at installation-time
    activation. Null for PoE cameras and for WiFi cameras that have not been activated yet.
     */
    wifiMacAddress?: string | null;
    /** The enterprise that the camera belongs to */
    enterpriseRid: string;
    /** The facility that the camera belongs to */
    facilityRid: string;
    /** The unit that the camera belongs to */
    unitRid?: string;
    mode: Schema;
    connectionType?: ConnectionType | null;
    /** Fall alert sensitivity level for this camera. Currently maps to the number of consecutive
    detections required before triggering a fall alert, but this interpretation is subject to
    change. Higher values mean fewer alerts. If null, the service default is used.
     */
    fallAlertSensitivity?: number | null;
    /** Whether health/offline alerting is active for this camera. When false, a known issue is being
    tracked: the stream is still processed, but offline/health pages are suppressed. camera-service
    automatically sets this back to true once the camera becomes healthy again.
     */
    isActive?: boolean;
    /** Free-text note about this camera - any operational context (for example, the reason it was set inactive).
     */
    description?: string | null;
};
export type GetCamerasResponse = {
    /** List of camera configurations */
    cameras: CameraDefinition[];
};
export type FailedCameraOperation = {
    /** The unique identifier of the camera that failed */
    cameraUid: string;
    /** Reason why the camera operation failed */
    errorReason: string;
};
export type BulkCameraOperationResponse = {
    /** Number of cameras successfully created or updated */
    successCount: number;
    /** Number of cameras that failed to be created or updated */
    failureCount: number;
    successfulCameras: CameraDefinition[];
    failedCameras: FailedCameraOperation[];
};
export type BulkCameraRequest = {
    cameras: {
        /** The unique Reolink identifier for the camera */
        uid: string;
        /** This is the human-readable name given to the camera during the unit allocation process. It may be null if
        the camera was not allocated to a unit, or the camera was allocated before we started using this field.
         */
        name?: string;
        /** The IP address of the camera. Absent for WiFi cameras that have been provisioned but not
        yet activated - their static IP is assigned at installation-time activation.
         */
        ipAddress?: string | null;
        /** The MAC address of the camera's ethernet (PoE port) interface. WiFi cameras have a separate
        MAC for the WiFi radio, which is not stored in this field.
         */
        macAddress: string;
        /** The MAC address of the camera's WiFi radio. Differs from the ethernet macAddress and is only
        discoverable once the camera has joined a WiFi network, so it is recorded at installation-time
        activation. Null for PoE cameras and for WiFi cameras that have not been activated yet.
         */
        wifiMacAddress?: string | null;
        /** The enterprise that the camera belongs to */
        enterpriseRid: string;
        /** The facility that the camera belongs to */
        facilityRid: string;
        /** The unit that the camera belongs to */
        unitRid?: string;
        mode: Schema;
        connectionType?: ConnectionType | null;
        /** Fall alert sensitivity level for this camera. Currently maps to the number of consecutive
        detections required before triggering a fall alert, but this interpretation is subject to
        change. Higher values mean fewer alerts. If null, the service default is used.
         */
        fallAlertSensitivity?: number | null;
        /** Whether health/offline alerting is active for this camera. When false, a known issue is being
        tracked: the stream is still processed, but offline/health pages are suppressed. camera-service
        automatically sets this back to true once the camera becomes healthy again.
         */
        isActive?: boolean;
        /** Free-text note about this camera - any operational context (for example, the reason it was set inactive).
         */
        description?: string | null;
    }[];
};
export type UpdateCameraRequest = {
    /** The human-readable name given to the camera during the unit allocation process */
    name?: string;
    /** The IP address of the camera */
    ipAddress?: string;
    /** The MAC address of the camera's ethernet (PoE port) interface. WiFi cameras have a separate
    MAC for the WiFi radio, which is not stored in this field.
     */
    macAddress?: string;
    /** The MAC address of the camera's WiFi radio. Differs from the ethernet macAddress and is only
    discoverable once the camera has joined a WiFi network, so it is recorded at installation-time
    activation. Null for PoE cameras and for WiFi cameras that have not been activated yet.
     */
    wifiMacAddress?: string | null;
    /** The enterprise that the camera belongs to */
    enterpriseRid?: string;
    /** The facility that the camera belongs to */
    facilityRid?: string;
    /** The unit that the camera belongs to */
    unitRid?: string;
    mode?: Schema;
    /** How the camera connects to the network.
    
    - POE: Wired Power-over-Ethernet camera (e.g. Reolink FE-P).
    - WIFI: Wireless camera joined to a Wi-Fi network (e.g. Reolink FE-W).
     */
    connectionType?: 'POE' | 'WIFI';
    /** Fall alert sensitivity level for this camera. Currently maps to the number of consecutive
    detections required before triggering a fall alert, but this interpretation is subject to
    change. Higher values mean fewer alerts. If null, the service default is used.
    Note: once set, this value cannot be unset via this API.
     */
    fallAlertSensitivity?: number | null;
    /** Whether health/offline alerting is active for this camera. When false, a known issue is being
    tracked: the stream is still processed, but offline/health pages are suppressed. camera-service
    automatically sets this back to true once the camera becomes healthy again.
     */
    isActive?: boolean;
    /** Free-text note about this camera - any operational context (for example, the reason it was set inactive).
     */
    description?: string | null;
};
export type BulkCameraUpdate = UpdateCameraRequest & {
    /** The unique identifier of the camera to update */
    uid?: string;
};
export type BulkUpdateCameraRequest = {
    cameras: BulkCameraUpdate[];
};
export type StreamOperationResponse = {
    /** The unique identifier of the camera */
    cameraUid: string;
    /** Indicates whether the camera was already in the target state (true) or the state was newly changed (false). */
    wasAlreadyInTargetState: boolean;
};
export type AdminValidationResponse = {
    /** Indicates whether the user is authorized as an administrator */
    authorized: boolean;
};
export type AdminTokenValidationRequest = {
    /** The token to be validated for administrator privileges */
    token: string;
};
export type CameraState = 'ONLINE_HEALTHY' | 'ONLINE_DEGRADED' | 'OFFLINE';
export type GetCameraHealthResponse = {
    /** List of camera health status results */
    cameraHealthResults: {
        cameraUid: string;
        facilityRid: string;
        unitRid: string;
        cameraState: 'ONLINE_HEALTHY' | 'ONLINE_DEGRADED' | 'OFFLINE';
        /** The rolling average number of segments per minute pulled from the camera over the health monitoring window.
        This is the value that is used to determine cameraState.
         */
        segmentsPerMinute: number;
        /** The most recent segments per minute observation for this camera, as opposed to the rolling average
        in segmentsPerMinute.
         */
        latestSegmentsPerMinute?: number;
        /** Approximate timestamp of the last segment that was pulled from the camera in milliseconds.
         */
        lastSegmentTimestampInMs: number;
        macAddress: string;
        /** Null for WiFi cameras that have been provisioned but not yet activated (their static
        IP is assigned at installation-time activation).
         */
        ipAddress?: string | null;
        mode: Schema;
        /** The camera's network signal strength, derived from its heartbeat probes alone and independent of
        cameraState. ONLINE_HEALTHY when enough pings were answered for a stable connection,
        ONLINE_DEGRADED when only some were, OFFLINE when none were or there is no recent probe.
        camera-service sets the thresholds.
         */
        signalStrength: CameraState;
        /** The camera's most recent heartbeat probe. Null when there is no recent probe.
         */
        lastProbe?: {
            /** Pings sent. */
            samplesSent: number;
            /** Pings answered. */
            samplesReceived: number;
            /** Average round-trip time of the answered pings. Null when no ping was answered. */
            averageLatencyInMs?: number | null;
            /** When the probe started, in milliseconds. */
            timestampInMs: number;
        } | null;
    }[];
};
export const {
    useGetCamerasQuery,
    useLazyGetCamerasQuery,
    useCreateOrUpdateCameraMutation,
    useCreateOrUpdateCamerasBulkMutation,
    useUpdateCamerasBulkMutation,
    useUpdateCameraMutation,
    useDeleteCameraMutation,
    useGetCameraQuery,
    useLazyGetCameraQuery,
    useStartCameraStreamMutation,
    useStopCameraStreamMutation,
    useValidateAdminTokenMutation,
    useGetCameraHealthQuery,
    useLazyGetCameraHealthQuery,
} = injectedRtkApi;
